import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Logo, { DepartmentLogo, CoBrandingLogo } from '@/components/ui/Logo';
import { useTheme } from '@/lib/theme';

/**
 * Brand Compliance Showcase Component
 * Demonstrates proper usage of Eduvos brand elements according to guidelines
 */
export const BrandShowcase: React.FC = () => {
  const { currentTheme, setTheme } = useTheme();

  return (
    <div className="space-y-8 p-6">
      <div className="text-center">
        <h1 className="font-primary text-4xl font-bold text-heritage-blue mb-2">
          Eduvos Brand Guidelines
        </h1>
        <p className="font-secondary text-lg text-heritage-blue/80">
          Impact & growth Platform Brand Compliance Showcase
        </p>
      </div>

      {/* Logo Usage Examples */}
      <Card className="p-6">
        <h2 className="font-primary text-2xl font-bold text-heritage-blue mb-4">
          Logo Usage
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-secondary font-semibold mb-3">Horizontal Layout</h3>
            <div className="bg-gray-50 p-4 rounded">
              <Logo variant="horizontal" size="md" />
            </div>
          </div>
          <div>
            <h3 className="font-secondary font-semibold mb-3">Stacked Layout</h3>
            <div className="bg-gray-50 p-4 rounded">
              <Logo variant="stacked" size="md" />
            </div>
          </div>
          <div>
            <h3 className="font-secondary font-semibold mb-3">Small Size (No Slogan)</h3>
            <div className="bg-gray-50 p-4 rounded">
              <Logo variant="horizontal" size="sm" />
            </div>
          </div>
          <div>
            <h3 className="font-secondary font-semibold mb-3">Department Lockup</h3>
            <div className="bg-gray-50 p-4 rounded">
              <DepartmentLogo 
                department="Faculty of Applied Science" 
                faculty="applied-science"
                size="md" 
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Color Palette */}
      <Card className="p-6">
        <h2 className="font-primary text-2xl font-bold text-heritage-blue mb-4">
          Color Palette
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="w-full h-20 bg-heritage-blue rounded mb-2"></div>
            <p className="font-secondary text-sm font-semibold">Heritage Blue</p>
            <p className="font-secondary text-xs text-gray-600">#15305D</p>
          </div>
          <div className="text-center">
            <div className="w-full h-20 bg-future-blue rounded mb-2"></div>
            <p className="font-secondary text-sm font-semibold">Future Blue</p>
            <p className="font-secondary text-xs text-gray-600">#29ABE2</p>
          </div>
          <div className="text-center">
            <div className="w-full h-20 bg-secondary-purple rounded mb-2"></div>
            <p className="font-secondary text-sm font-semibold">Secondary Purple</p>
            <p className="font-secondary text-xs text-gray-600">#8764E3</p>
          </div>
          <div className="text-center">
            <div className="w-full h-20 bg-secondary-green rounded mb-2"></div>
            <p className="font-secondary text-sm font-semibold">Secondary Green</p>
            <p className="font-secondary text-xs text-gray-600">#85BF3F</p>
          </div>
        </div>
      </Card>

      {/* Typography Examples */}
      <Card className="p-6">
        <h2 className="font-primary text-2xl font-bold text-heritage-blue mb-4">
          Typography
        </h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-primary text-3xl font-bold text-heritage-blue">
              Larken Bold - Primary Headlines
            </h3>
            <p className="font-secondary text-base text-heritage-blue/80">
              Cocogoose Regular - Body text and descriptions
            </p>
          </div>
          <div>
            <h4 className="font-primary text-xl font-medium text-heritage-blue">
              Larken Medium - Subheadings
            </h4>
            <p className="font-secondary text-sm text-heritage-blue/70">
              Cocogoose - Smaller body text and captions
            </p>
          </div>
        </div>
      </Card>

      {/* Button Variants */}
      <Card className="p-6">
        <h2 className="font-primary text-2xl font-bold text-heritage-blue mb-4">
          Button Variants
        </h2>
        <div className="flex flex-wrap gap-3">
          <Button variant="default">Heritage Blue</Button>
          <Button variant="secondary">Future Blue</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="scale">Scale Pillar</Button>
          <Button variant="quality">Quality Pillar</Button>
          <Button variant="culture">Culture Pillar</Button>
          <Button variant="impact">Impact Pillar</Button>
        </div>
      </Card>

      {/* Faculty Themes */}
      <Card className="p-6">
        <h2 className="font-primary text-2xl font-bold text-heritage-blue mb-4">
          Faculty Themes
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button 
            variant="outline" 
            onClick={() => setTheme('law-commerce')}
            className="h-auto p-4 flex-col"
          >
            <div className="w-8 h-8 bg-faculty-law rounded mb-2"></div>
            Law & Commerce
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setTheme('humanities')}
            className="h-auto p-4 flex-col"
          >
            <div className="w-8 h-8 bg-faculty-humanities rounded mb-2"></div>
            Humanities
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setTheme('applied-science')}
            className="h-auto p-4 flex-col"
          >
            <div className="w-8 h-8 bg-faculty-applied-science rounded mb-2"></div>
            Applied Science
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setTheme('it')}
            className="h-auto p-4 flex-col"
          >
            <div className="w-8 h-8 bg-faculty-it rounded mb-2"></div>
            Information Technology
          </Button>
        </div>
        <p className="text-sm text-heritage-blue/70 mt-3">
          Current theme: <span className="font-semibold">{currentTheme.name}</span>
        </p>
      </Card>

      {/* Spacing Guidelines */}
      <Card className="p-6">
        <h2 className="font-primary text-2xl font-bold text-heritage-blue mb-4">
          Spacing Guidelines (X-Unit System)
        </h2>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-x h-4 bg-heritage-blue rounded"></div>
            <span className="font-secondary text-sm">1X Unit (1rem) - Base spacing</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-2x h-4 bg-future-blue rounded"></div>
            <span className="font-secondary text-sm">2X Units (2rem) - Minimum logo clear space</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-3x h-4 bg-secondary-purple rounded"></div>
            <span className="font-secondary text-sm">3X Units (3rem) - Section spacing</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-4x h-4 bg-secondary-green rounded"></div>
            <span className="font-secondary text-sm">4X Units (4rem) - Large section spacing</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BrandShowcase;
